import { Box, ButtonBase, Typography } from "@mui/material";
import {
	isImplausible,
	isOutOfSpec,
	type MeasurementValues,
	POSITIONS,
	type Position,
	toNumber,
} from "@/pages/sawlines/utils/qualityCheckMeasurements";
import { toFraction32 } from "@/utils/fraction";
import type { SawShiftBundle } from "@/pages/sawlines/repositories/sawBundlesRepository";
import { QualityStatusChip } from "./QualityStatusChip";

// Point's spot on the bundle end.
const DOTS: Record<Position, [number, number]> = {
	TL: [8, 7],
	TR: [26, 7],
	MC: [17, 13],
	BL: [8, 19],
	BR: [26, 19],
};

export const FaceIcon = ({
	dots,
	scale = 1,
}: {
	dots: Partial<Record<Position, string>>;
	scale?: number;
}) => (
	<svg width={34 * scale} height={26 * scale} viewBox="0 0 34 26" aria-hidden>
		<rect
			x="1"
			y="1"
			width="32"
			height="24"
			rx="4"
			fill="#fff"
			stroke="#9e9e9e"
			strokeWidth="1.5"
		/>
		{POSITIONS.map(({ key }) => (
			<circle
				key={key}
				cx={DOTS[key][0]}
				cy={DOTS[key][1]}
				r={dots[key] ? 4 : 2.5}
				fill={dots[key] ?? "#bdbdbd"}
			/>
		))}
	</svg>
);

type PointState = "empty" | "typing" | "odd" | "noTolerance" | "out" | "ok";

// `typing`: judged only once the operator leaves the point (blur)
const pointState = (
	cut: SawShiftBundle,
	text: string,
	typing: boolean,
): PointState =>
	toNumber(text) == null
		? "empty"
		: typing
			? "typing"
			: isImplausible(cut, text)
				? "odd"
				: cut.MinLength == null || cut.MaxLength == null
					? "noTolerance"
					: isOutOfSpec(cut, text)
						? "out"
						: "ok";

const BADGE: Partial<Record<PointState, string>> = {
	odd: "Check value",
	out: "Out",
	ok: "OK",
};

const DOT_COLOR: Partial<Record<PointState, string>> = {
	typing: "#757575",
	odd: "#f2a900",
	noTolerance: "#3d5e9e",
	out: "#c62828",
	ok: "#2e7d32",
};

export const faceDots = (
	cut: SawShiftBundle,
	values: MeasurementValues,
	typing: Position | null,
) =>
	Object.fromEntries(
		POSITIONS.map(({ key }) => [
			key,
			DOT_COLOR[pointState(cut, values[key], key === typing)],
		]),
	) as Partial<Record<Position, string>>;

// The form not typed: decimal under a fraction, fraction under a decimal.
const otherForm = (text: string, value: number) => {
	const exact = Number.isInteger(value * 32) ? "=" : "≈";
	return text.includes("/")
		? `${exact} ${value.toFixed(2)}″`
		: `${exact} ${toFraction32(value)}`;
};

export const MeasurementFace = ({
	cut,
	values,
	position,
	typing,
	disabled,
	onSelect,
}: {
	cut: SawShiftBundle;
	values: MeasurementValues;
	position: Position;
	typing: Position | null;
	disabled: boolean;
	onSelect: (position: Position) => void;
}) => (
	<Box
		sx={{
			// no minHeight 0: short screens scroll the card, rows never overlap
			flex: 1,
			display: "grid",
			gridTemplateRows: "repeat(5, minmax(52px, 1fr))",
			gap: 0.75,
			px: 1.5,
			py: 1,
		}}
	>
		{POSITIONS.map(({ key, label }) => {
			const text = values[key];
			const value = toNumber(text);
			const state = pointState(cut, text, key === typing);
			const odd = state === "odd";
			const selected = position === key;
			const color = odd ? "#8a5a00" : state === "out" ? "#c62828" : "#2e7d32";
			const badge = BADGE[state];
			return (
				<ButtonBase
					key={key}
					disabled={disabled}
					onClick={() => onSelect(key)}
					aria-label={`${label}: ${value == null ? "Not measured" : `${text} inches`}${badge ? `, ${badge}` : ""}`}
					aria-pressed={selected}
					sx={{
						display: "grid",
						gridTemplateColumns: "34px 96px minmax(0, 1fr) auto",
						alignItems: "center",
						columnGap: 1.5,
						px: 1.5,
						minHeight: 0,
						textAlign: "left",
						border: "2px solid",
						borderColor: selected
							? "primary.main"
							: badge
								? `${color}55`
								: "divider",
						borderRadius: 2,
						bgcolor: odd
							? "#fff4dc"
							: badge
								? `${color}12`
								: "background.paper",
						boxShadow: selected ? "0 0 0 3px #2e7d3226" : "none",
						"&.Mui-focusVisible": {
							outline: "3px solid",
							outlineColor: "primary.main",
						},
					}}
				>
					<FaceIcon dots={{ [key]: "#2e7d32" }} />
					<Typography
						sx={{
							textTransform: "uppercase",
							fontSize: 13,
							lineHeight: 1.2,
							color: "text.secondary",
						}}
					>
						{label}
					</Typography>
					<Box sx={{ minWidth: 0 }}>
						<Typography
							sx={{
								fontSize: 28,
								lineHeight: 1.1,
								fontWeight: 700,
								fontVariantNumeric: "tabular-nums",
								whiteSpace: "nowrap",
							}}
						>
							{text === "" ? "—" : `${text}″`}
						</Typography>
						<Typography
							sx={{
								fontSize: 13,
								color:
									selected && value == null ? "primary.dark" : "text.secondary",
							}}
						>
							{value != null
								? otherForm(text, value)
								: selected
									? "Type inches, then a fraction"
									: "Not measured"}
						</Typography>
					</Box>
					{badge && <QualityStatusChip label={badge} color={color} />}
				</ButtonBase>
			);
		})}
	</Box>
);
